import type { UserProfile } from "../types/auth";

/**
 * Page permissions an organization admin can withhold from a staff member.
 * Mirrors `Constants/Permissions.cs` in the API — change both together.
 */
export const PERMISSIONS = {
  calls: "calls",
  video: "video",
  messaging: "messaging",
  notes: "notes",
  rosters: "rosters",
  schedule: "schedule",
  // Spelled with an underscore, byte-for-byte as `Permissions.TrustedContacts` in the API. The
  // comparison there is case-insensitive but not separator-insensitive, and a mismatch fails open
  // here and closed at the API — a page that renders and then 403s on every request.
  trustedContacts: "trusted_contacts",
  duplicates: "duplicates",
} as const;

export type Permission = (typeof PERMISSIONS)[keyof typeof PERMISSIONS];

/**
 * What each page is called when we have to name it — currently the access-denied screen.
 *
 * Mirrors the labels in `Permissions.All` on the API, which is what the admin's Page Access
 * toggles already show, so a coach reads the same name in both places. Keeping it here rather
 * than passing a string per route stops the name from being spelled a fifth way.
 */
export const PERMISSION_LABELS: Record<Permission, string> = {
  calls: "Calls",
  video: "Video",
  messaging: "Messaging",
  notes: "Latest Notes",
  rosters: "Roster Planning",
  schedule: "Schedule",
  trusted_contacts: "Trusted Contacts",
  duplicates: "Merge Manager",
};

/**
 * Whether this profile may reach `permission`.
 *
 * **Absent means allowed**, in three separate cases that all have to fail open:
 *
 * - the profile is still loading, so nothing is known yet
 * - the API predates this feature and sends no `permissions` field
 * - the field was lost somewhere in between
 *
 * Denying on absence would be indistinguishable from every coach in every organization being
 * restricted at once — the same reasoning as `messagingEnabled` in `types/auth.ts`.
 *
 * This is presentation only. The API enforces the same restriction with `RequirePermissionAttribute`,
 * which is what actually stops a bookmark or a pasted URL.
 */
export const hasPermission = (
  profile: UserProfile | undefined,
  permission: Permission
): boolean => {
  const permissions = profile?.permissions;
  if (!permissions) return true;
  return permissions.includes(permission);
};
